import { useEffect } from "react";
import { Typography, Box, Chip } from "@mui/material";
import { useDispatch } from "react-redux";
import { ISubcontract } from "../../../interface/subcontract";
import { setTotalByMonth } from "../../../redux/actions/forecastActions";
import getForecastMonthList from "../../../tools/forecast/getForecastMonthList";
import CustomDataGrid from "../../customDataGrid/CustomDataGrid";

const SubcontractData = ({ 
    forecastDates, 
    type = "Inconnu", 
    searchLabel = "", 
    setSelectedSubcontract, 
    setOpenModal, 
    setEditId, 
    subcontracts = [], 
    selectedSubcontract = [] 
}: any) => {
    
    const dispatch = useDispatch();
    const monthList = getForecastMonthList(forecastDates);

    // --- COLONNES ---
    const columns = [
        { field: "label", headerName: "Libellé", flex: 1, minWidth: 200 },
        { field: "supplier", headerName: "Fournisseur", width: 150 },
        { field: "agency", headerName: "Agence", width: 120 },
        ...monthList.map((m: any) => ({ 
            field: m.field, 
            headerName: m.name, 
            align: 'center',
            width: 130,
            renderCell: (params: any) => <div dangerouslySetInnerHTML={{ __html: params.value || "0 €" }} />
        }))
    ];

    // --- LIGNES ---
    const subcontractRows = (subcontracts || []).map((s: ISubcontract) => {
        const row: any = { 
            id: s._id, 
            label: s.label || "N/C", 
            supplier: s.supplier?.name || "N/C", 
            agency: s.agency || "N/A" 
        };
        monthList.forEach((m: any) => {
            const monthsSource = s.months as any;
            let val = 0;
            if (Array.isArray(monthsSource)) {
                val = monthsSource.find((item: any) => item.label === m.field)?.amount || 0;
            } else if (monthsSource) {
                val = monthsSource[m.field]?.amount ?? monthsSource[m.field] ?? 0;
            }
            row[m.field] = `<div style="font-weight: 700; text-align: center;">${Number(val).toLocaleString()} €</div>`;
            row[`raw_${m.field}`] = Number(val);
        });
        return row;
    });

    // --- TOTAUX ---
    const totalByMonth = monthList.reduce((acc: any, m: any) => {
        acc[m.field] = subcontractRows.reduce((sum: number, row: any) => sum + (row[`raw_${m.field}`] || 0), 0);
        return acc;
    }, {});

    useEffect(() => {
        if (type && Object.keys(totalByMonth).length > 0) {
            dispatch(setTotalByMonth(`Sous-traitance-${type}`, totalByMonth));
        }
    }, [totalByMonth, type]);

    const totalRow = {
        id: "total",
        label: `<span style="color: #f59e0b; font-weight: 900">TOTAL ${type.toUpperCase()}</span>`,
        supplier: "",
        agency: "",
        ...Object.fromEntries(Object.entries(totalByMonth).map(([k, v]: [string, any]) => [
            k, `<div style="text-align: center;"><span style="color: #f59e0b; font-weight: 900">${Number(v).toLocaleString()} €</span></div>`
        ]))
    };

    // --- SÉCURITÉ AFFICHAGE ---
    // Si pas de data, on affiche quand même la structure ou un message pour ne pas être "invisible"
    const rows = [...subcontractRows, totalRow];

    return (
        <Box sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, px: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#64748b", textTransform: 'uppercase' }}>{type}</Typography>
                <Chip label={`${subcontractRows.length} lignes`} size="small" sx={{ height: 20, fontSize: '0.7rem', bgcolor: "#e2e8f0" }} />
            </Box>

            <CustomDataGrid 
                rows={rows} 
                columns={columns} 
                checkboxSelection 
                disableRowSelectionOnClick 
                rowSelectionModel={selectedSubcontract}
                onRowSelectionModelChange={(newSelection: any) => setSelectedSubcontract(newSelection)}
                isRowSelectable={(params: any) => params.row.id !== 'total'}
                onCellClick={(params: any) => {
                    if (params.row.id !== 'total' && params.field !== '__check__') {
                        setEditId(params.row.id);
                        setOpenModal(true);
                    }
                }}
                sx={{
                    bgcolor: 'white', borderRadius: '12px', border: "1px solid #e2e8f0",
                    "& .MuiDataGrid-columnHeaders": { bgcolor: "#f8fafc" }
                }}
            />
        </Box>
    );
};

export default SubcontractData;